import { useEffect, useCallback } from "react";
import confetti from "canvas-confetti";

interface CelebrationAnimationProps {
  type: "delivered" | "all_paid" | "gift_on_way";
  trigger: boolean;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function CelebrationAnimation({ type, trigger }: CelebrationAnimationProps) {
  const fireConfetti = useCallback(() => {
    const duration = 3000;
    const end = Date.now() + duration;

    const colors = type === "delivered" 
      ? ["#8B5CF6", "#A855F7", "#C084FC", "#E879F9", "#F0ABFC"] // Purple shades for delivery
      : type === "all_paid"
      ? ["#22C55E", "#4ADE80", "#86EFAC", "#BBF7D0", "#8B5CF6"] // Green + purple for all paid
      : ["#F59E0B", "#FBBF24", "#FCD34D", "#FDE68A", "#8B5CF6"]; // Gold + purple for on the way

    const frame = () => {
      if (Date.now() > end) return;

      // Fire from left side
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: colors,
        shapes: type === "delivered" ? ["star", "circle"] : ["circle"],
        scalar: 1.2,
      });

      // Fire from right side
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: colors,
        shapes: type === "delivered" ? ["star", "circle"] : ["circle"],
        scalar: 1.2,
      });

      requestAnimationFrame(frame);
    };

    // Initial burst
    if (type === "delivered") {
      // Big celebration for delivery
      confetti({
        particleCount: 100,
        spread: 100,
        origin: { y: 0.6 },
        colors: colors,
        shapes: ["star", "circle"],
        scalar: 1.5,
      });
    } else if (type === "all_paid") {
      // Celebration burst for all paid
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 },
        colors: colors,
        scalar: 1.3,
      });
    } else {
      // Gentle celebration for gift on way
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.6 },
        colors: colors,
        scalar: 1.1,
      });
    }

    frame();
  }, [type]);

  useEffect(() => {
    if (trigger) {
      // Small delay for visual impact
      const timer = setTimeout(fireConfetti, 300);
      return () => clearTimeout(timer);
    }
  }, [trigger, fireConfetti]);

  return null; // This component doesn't render anything visible
}

// Hook for easy usage
export function useCelebration() {
  const celebrate = useCallback(function(type: "delivered" | "all_paid" | "gift_on_way") {
    const duration = 3000;
    const end = Date.now() + duration;

    const colors = type === "delivered" 
      ? ["#8B5CF6", "#A855F7", "#C084FC", "#E879F9", "#F0ABFC"]
      : type === "all_paid"
      ? ["#22C55E", "#4ADE80", "#86EFAC", "#BBF7D0", "#8B5CF6"]
      : ["#F59E0B", "#FBBF24", "#FCD34D", "#FDE68A", "#8B5CF6"];

    function frame() {
      if (Date.now() > end) return;

      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: colors,
        shapes: type === "delivered" ? ["star", "circle"] : ["circle"],
        scalar: 1.2,
      });

      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: colors,
        shapes: type === "delivered" ? ["star", "circle"] : ["circle"],
        scalar: 1.2,
      });

      requestAnimationFrame(frame);
    }

    // Initial burst
    const particleCount = type === "delivered" ? 100 : type === "all_paid" ? 80 : 50;
    confetti({
      particleCount,
      spread: type === "delivered" ? 100 : 70,
      origin: { y: 0.6 },
      colors: colors,
      shapes: type === "delivered" ? ["star", "circle"] : ["circle"],
      scalar: type === "delivered" ? 1.5 : 1.2,
    });

    frame();
  }, []);

  return { celebrate };
}
